import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faWandMagicSparkles, faPuzzle } from "@fortawesome/pro-solid-svg-icons";
import { faHouse } from "@fortawesome/pro-regular-svg-icons";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context";
import Tooltip from "@/components/tooltip";

import Card from "@/components/card/index.tsx";
import CheckBox from "@/components/checkbox";
import ProjectEditButton from "@/components/buttons/project-edit-button";
import shellHighlight from "../../styles/shell-highlight.module.css";
import ValidationPack from "@/pages/takeoff/layouts/validation/models/validation-pack";
import ValidationState from "@/models/enums/validation-state";

interface Calcul2PdfsuperposeCardActionProps {
    storeysFileDataHasScale: boolean;
    hasCalcul2: boolean;
    validationPacks: ValidationPack[];
    isLoading: boolean;
    onClick: () => void;
    onIndicatorCheckChanged: (indicatorKeyname: string, checked: boolean) => void;
}

const Calcul2PdfsuperposeCardAction: React.FC<Calcul2PdfsuperposeCardActionProps> = ({
    storeysFileDataHasScale,
    hasCalcul2,
    validationPacks,
    isLoading,
    onClick,
    onIndicatorCheckChanged
}) => {
    const { isLoading: isViewerLoading } = useViewerContext();
    const toValidate = validationPacks.filter(
        p => p.validation.state === ValidationState.ToValidate
    ).length;

    let content: React.ReactNode;

    if (hasCalcul2) {
        content = (
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <ProjectEditButton
                    variant='simple'
                    size='sm'
                    onClick={onClick}
                    disabled={isLoading || !storeysFileDataHasScale}
                >
                    <div className='flex flex-row gap-x-2 items-center'>
                        <div className='flex flex-row gap-x-1 items-baseline'>
                            <FontAwesomeIcon icon={faHouse} />
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                            <FontAwesomeIcon icon={faCheck} className='text-green-500' />
                        </div>

                        <span>Calcul automatique</span>
                    </div>
                </ProjectEditButton>

                <div>
                    <span>Visualisez</span>

                    <ul className='list-disc list-inside pl-2 text-sm'>
                        <div>
                            <CheckBox
                                disabled={isViewerLoading}
                                onChange={(e) =>
                                    onIndicatorCheckChanged('SHAB', e.target.checked)
                                }
                                checkedColors='checked:bg-[#90ee90] checked:border-[#90ee90]'
                            />
                            <span className='pl-3'>SHAB brute</span>
                        </div>

                        <div>
                            <CheckBox
                                disabled={isViewerLoading}
                                onChange={(e) =>
                                    onIndicatorCheckChanged('SDP', e.target.checked)
                                }
                                checkedColors='checked:bg-[#bfbfbf] checked:border-[#bfbfbf]'
                            />
                            <span className='pl-3'>SDP</span>
                        </div>

                        <div>
                            <CheckBox
                                disabled={isViewerLoading}
                                onChange={(e) =>
                                    onIndicatorCheckChanged('ST', e.target.checked)
                                }
                                checkedColors='checked:bg-[#ff9b98] checked:border-[#ff9b98]'
                            />
                            <span className='pl-3'>ST</span>
                        </div>

                        <div>
                            <CheckBox
                                disabled={isViewerLoading}
                                onChange={(e) =>
                                    onIndicatorCheckChanged('SHO', e.target.checked)
                                }
                                checkedColors='checked:bg-[#595959] checked:border-[#595959]'
                            />
                            <span className='pl-3'>SHO</span>
                        </div>

                        <div>
                            <CheckBox
                                disabled={isViewerLoading}
                                onChange={(e) =>
                                    onIndicatorCheckChanged('SHOB', e.target.checked)
                                }
                                checkedColors='checked:bg-[#7f7f7f] checked:border-[#7f7f7f]'
                            />
                            <span className='pl-3'>SHOB</span>
                        </div>
                    </ul>
                </div>
            </Card>
        );
    } else if (toValidate === 0) {
        content = (
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <ProjectEditButton
                    variant='simple'
                    size='sm'
                    onClick={onClick}
                    disabled={isLoading || !storeysFileDataHasScale}
                >
                    <div className='flex flex-row gap-x-2 items-center'>
                        <div className='flex flex-row gap-x-1 items-baseline'>
                            <FontAwesomeIcon icon={faHouse} />
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                        </div>

                        <span>Finalisez le calcul</span>
                    </div>
                </ProjectEditButton>

                <span>Tous les étages ont été traités.</span>
            </Card>
        );
    } else {
        content = (
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <span>Validez toutes les destinations de pièces pour obtenir</span>

                <ul className='list-disc list-inside pl-2 text-sm'>
                    <li>SHAB brute</li>
                    <li>SDP</li>
                    <li>ST</li>
                    <li>SHO</li>
                    <li>SHOB</li>
                </ul>

                <div className='flex flex-row items-center justify-between'>
                    <span>{validationPacks.length - toValidate}/{validationPacks.length} des étages ont été traités.</span>
                    <FontAwesomeIcon icon={faPuzzle}
                                     className={`${shellHighlight.hint} rounded text-xl text-slate-900 cursor-help focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500`}
                                     aria-label='Repérer les boutons des métrés coques'
                                     data-tooltip-id='shell-icon-tooltip'/>   
                </div>
            </Card>
        );
    }

    return (
        <>
            <Tooltip
                id='shell-icon-tooltip'
                place='bottom-end'
                key='thermal-icon-tooltip'
            >
                Vous devez régler les destinations de chaque zone
            </Tooltip>

            {content}
        </>
    );
};

export default Calcul2PdfsuperposeCardAction;